import { type Logger } from 'loglevel';
import { type ServiceTokens } from '../../container';

/**
 * Represents a plugin that can be applied to a base service.
 * @template TBase The type of the base service.
 * @template TExtension The type of the extension provided by the plugin.
 */
export type Plugin<TBase, TExtension> = {
  apply: <T extends TBase>(base: T) => T & TExtension;
  name: string;
  destroy: () => void;
};

type PluggableServiceDependencies = {
  [ServiceTokens.Logger]: Logger;
};

/**
 * A base class for services that support plugins.
 * This class provides functionality to add, remove, and check for plugins.
 */
export class PluggableService {
  protected logger: Logger;

  protected plugins: Map<string, Plugin<unknown, unknown>> = new Map();

  constructor({ logger }: PluggableServiceDependencies) {
    this.logger = logger;
  }

  /**
   * Adds a plugin to the service.
   * @template T The type of the extension provided by the plugin.
   * @param plugin The plugin to add.
   * @returns The enhanced service with the plugin's functionality.
   * @throws Error if the plugin is already added or if adding the plugin fails.
   */
  addPlugin<T extends this, B>(plugin: Plugin<this, B>): T & B {
    if (this.plugins.has(plugin.name)) {
      const error = new Error(`Plugin ${plugin.name} is already added.`);
      this.logger.error(error);
      throw error;
    }
    try {
      const enhanced = plugin.apply(this);
      this.plugins.set(plugin.name, plugin);
      return enhanced as T & B;
    } catch (error) {
      this.logger.error(`Failed to add plugin ${plugin.name}:`, error);
      throw new Error(
        `Failed to add plugin ${plugin.name}: ${(error as Error).message}`,
      );
    }
  }

  /**
   * Removes a plugin from the service.
   * @param pluginName The name of the plugin to remove.
   */
  removePlugin(pluginName: string): void {
    const plugin = this.plugins.get(pluginName);
    if (plugin) {
      plugin.destroy();
      this.plugins.delete(pluginName);
    }
  }

  hasPlugin(pluginName: string): boolean {
    return this.plugins.has(pluginName);
  }
}
